<script setup lang="ts">
import useScrollHook, { SCROLL_TYPE_ENUMS } from '@/hooks/useScrollHook'
import SwiperCarousel from '@/views/index/SwiperCarousel.vue'
import XgVideoPlayer from '@/components/XgVideoPlayer.vue'
import ShareOverlay from '@/components/ShareOverlay.vue'
import { APP_ID } from '@/enums'
import { getDownloadLink } from '@/api'
import { copyToClipboard } from '@/utils'
import { isMobile, isWeixin } from '@/utils/systemInfo'
import { onMounted, onUnmounted } from '#imports'
import QRCode from 'qrcode'

const router = useRouter()

isMobile() && router.push('/homeH5')

const imgList = ['/images/home/banner-1.png', '/images/home/banner-2.png', '/images/home/banner-3.png']
const { scrollTo, handleScroll } = useScrollHook()

onMounted(() => {
  window.addEventListener('mousewheel', handleScroll, { passive: false })
})

onUnmounted(() => {
  window.removeEventListener('mousewheel', handleScroll)
})

function downloadApp(appId: APP_ID) {
  getDownloadLink({ appId }).then((res) => {
    if (appId === APP_ID.IOS) {
      copyToClipboard(res.url, '下载地址已复制到剪贴板, 请打开safari浏览器进行下载~~')
    } else {
      window.open(res.url)
    }
  })
}

const qrCanvas = ref<HTMLCanvasElement>()

onMounted(() => {
  QRCode.toCanvas(qrCanvas.value, location.href, {
    width: 114,
    scale: 2,
    margin: 2
  })
  const canvas = qrCanvas.value
  const ctx = canvas.getContext('2d')
  const logo = new Image()
  logo.src = '/images/download/download-logo.png'
  const logoSize = 25
  logo.onload = () => {
    const logoX = (canvas.width - logoSize) / 2
    const logoY = (canvas.height - logoSize) / 2
    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize)
  }
})
</script>

<template>
  <div class="container">
    <!-- 一屏：视频 -->
    <div class="video-container">
      <!-- 微信浏览器内提示箭头 -->
      <ClientOnly>
        <ShareOverlay v-if="isWeixin()" />
        <XgVideoPlayer
          video-url="https://static.hzpt712.hzqumokj.cn/oss_browser/xkira/web/official/res/bg_angel_horizontal_4k_1226.mp4"
          poster-url="/images/home/poster-img.png"
        />
      </ClientOnly>
      <!-- 下载 -->
      <div class="download-container">
        <!-- title -->
        <div class="title-container">
          <img class="title" src="/images/home/title.png" alt="" />
        </div>
        <!-- 下载区 -->
        <div class="download-box">
          <span class="download-box-toast">扫码下载盒子</span>
          <canvas ref="qrCanvas" class="qr-code"></canvas>
          <div class="download-btn-box">
            <div class="download-btn download-btn-ios" @click="downloadApp(APP_ID.IOS)"></div>
            <div class="download-btn download-btn-android" @click="downloadApp(APP_ID.ANDROID)"></div>
          </div>
          <div class="download-btn download-btn-win" @click="downloadApp(APP_ID.WIN)"></div>
        </div>
      </div>
      <div class="arrow-overlay">
        <img class="arrow" src="/images/home/arrow.gif" @click="scrollTo(SCROLL_TYPE_ENUMS.BOTTOM)" />
      </div>
    </div>
    <!-- 二屏：图片轮播 -->
    <div class="carousel-box">
      <SwiperCarousel :images="imgList" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  width: 100vw;
  min-width: 1440px;

  .video-container {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;

    .download-container {
      z-index: 10;
      position: absolute;
      top: 340px;
      right: 200px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .title-container {
        width: 501px;
        height: 140px;
        margin-bottom: 60px;

        .title {
          position: absolute;
          width: 501px;
          height: 140px;
        }
      }

      .download-box {
        width: 100%;
        height: 114px;
        gap: 16px;
        display: flex;
        justify-content: flex-end;

        .download-box-toast {
          display: inline-block;
          color: #fff;
          font-size: 12px;
          width: 16px;
          line-height: 19px;
        }

        .qr-code {
          width: 114px;
          height: 114px;
          border-radius: 8px;
        }

        .download-btn-box {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          width: 153px;
        }

        .download-btn {
          width: 153px;
          height: 52px;
          cursor: pointer;
          transition: all 0.3s;

          &.download-btn-android {
            background-image: url('/images/home/downloadBtnAndroid.png');
            background-size: cover;

            &:hover {
              background-image: url('/images/home/downloadBtnAndroidHighlight.png');
            }
          }

          &.download-btn-ios {
            background-image: url('/images/home/downloadBtnIos@2x.png');
            background-size: cover;

            &:hover {
              background-image: url('/images/home/downloadBtnIosHighlight.png');
            }
          }

          &.download-btn-win {
            width: 114px;
            height: 114px;
            background-image: url('/images/home/downloadBtnWin.png');
            background-size: cover;

            &:hover {
              background-image: url('/images/home/downloadBtnWinHighlight.png');
            }
          }
        }
      }
    }

    .arrow-overlay {
      z-index: 10;
      position: absolute;
      bottom: 0;
      height: 250px;
      width: 100%;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(18, 21, 23, 1));

      .arrow {
        animation: bounce 2s infinite;
        bottom: 20px;
        height: 100px;
        left: 50%;
        position: absolute;
        transform: translate(-50%);
        width: 40px;
        cursor: pointer;
      }
    }
  }

  .carousel-box {
    width: 100%;
    height: 100vh;
    background-image: url('/images/home/carousel-bg.png');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
}
</style>
